/**
 * What: Everything the Study Builder page owns outside the editor: the bootstrap read, the page lifecycle
 *   and what the editor starts from. See `pages/study-builder.page.ts` for the declaration.
 * Transport / data-flow: One TanStack query (`fetchInitialBuilderState`) goes to `usePageLifecycle`, which
 *   checks it and tells the page when it is ready or has a newer version. No effects, no form writes here.
 */
import type { StudyAuthoringRecord } from '@testfeed/shopify-contracts'
import { useCallback, useMemo, useState } from 'react'
import { getAppBridgeShop } from '../../authenticatedShopBootstrap'
import { replaceStudyBuilderUrl, type ShopifyAppViewState } from '../../resultsNavigation'
import { page } from '../../pages/study-builder.page'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { useQuery } from '@tanstack/react-query'
import { usePageLifecycle } from '../usePageLifecycle'
import { retryNetworkOnly } from '../../page-state'
import { buildStudyBuilderInitialStateQueryKey, fetchInitialBuilderState } from './studyBuilderStatus'
import {
  assertDirectStudyBuilderRoute,
  assertStudyBuilderStartable,
  resolveStudyBuilderStart,
} from './studyBuilderStart'

type StudyBuilderView = Extract<ShopifyAppViewState, { view: 'study-builder' }>

export function useStudyBuilderPage(input: {
  appView: StudyBuilderView
  visit: string
  shopify: ShopifyAppBridge
  expectedShop: string | null
  scope: VerifiedTestFeedConnectionScope | null
}) {
  const { appView, visit, shopify, expectedShop, scope } = input
  const editSetupId = appView.setupId?.trim() || null
  const appBridgeShop = expectedShop ?? getAppBridgeShop(shopify)
  const sourceKey = appView.studyId ? `study:${appView.studyId}` : `intent:${appView.draftId ?? ''}`
  const route = {
    studyId: appView.studyId,
    setupId: appView.setupId,
    draftId: appView.draftId,
    entry: appView.entry,
    forkParentPacketId: appView.forkParentPacketId,
    forkChangeReason: appView.forkChangeReason,
  }
  const initialState = useQuery({
    queryKey: [
      ...buildStudyBuilderInitialStateQueryKey(
        scope?.shopDomain || appBridgeShop || 'no-shop',
        scope?.orgId ?? null,
        scope?.connectionId ?? null,
      ),
      sourceKey,
    ],
    queryFn: async ({ signal }) => {
      const identity = assertDirectStudyBuilderRoute(route)
      const read = await fetchInitialBuilderState(
        shopify,
        signal,
        null,
        appBridgeShop,
        identity.kind === 'creation' ? identity.creationIntentId : null,
        { studyId: identity.kind === 'study' ? identity.studyId : undefined, isNew: identity.kind === 'creation' },
      )
      // Failures that must reach the lifecycle as a query error (named and reported), never a render-time branch.
      assertStudyBuilderStartable(read, route)
      return read
    },
    enabled: !!appBridgeShop && !!scope,
    ...shopifyQueryPolicies.builderBootstrap,
    retry: retryNetworkOnly,
  })
  const lifecycle = usePageLifecycle(
    page,
    visit,
    shopify,
    { 'initial-state': initialState },
    { setupId: editSetupId, draftId: appView.studyId ?? appView.draftId },
  )
  const data = lifecycle.data.applied['initial-state']
  const start = useMemo(() => (data && !data.deletion ? resolveStudyBuilderStart(data) : null), [data])
  // The acknowledged create/save reply promotes the creation intent to its exact direct study ID.
  const onDraftSaved = useCallback(
    (record?: StudyAuthoringRecord) => {
      if (!record) return
      replaceStudyBuilderUrl({
        view: 'study-builder',
        studyId: record.studyId,
        step: appView.step,
        section: appView.section,
      })
    },
    [appView],
  )
  return { lifecycle, initialState, data, start, editSetupId, onDraftSaved }
}
